/**
 * HER PHONE ALERTS, the part of the dancer home's switch that needs no
 * Firebase SDK (DECISIONS 135, Bobbie, 2026-10-18: "dancers should get a
 * notification if they get a message").
 *
 * The switch itself is components in pages/landing/dancer/DancerPushToggle.tsx.
 * The token is minted by lib/dancerPush.ts, which imports firebase/messaging
 * and is only loaded when she taps, or when this device already has her
 * alerts on and the browser may have rotated its token. This file never
 * imports it, so the dancer home's own chunk stays free of messaging.
 *
 * What lives here: this device's record of the token it registered (one
 * record, the server decides whose it is and whether it is still on), the
 * plain answer to "can this phone get alerts at all", and the label the
 * service worker's preview store keeps for a dancer sign-in.
 */
import { pushDeviceGuidance, type PushDeviceGuidance } from './pushRecovery.ts';

const DEVICE_KEY = 'ldg_dancer_push_device:v1';

/**
 * The preview store (lib/pushPreview.ts) keeps a label beside the claim id.
 * A grown-up's label is her uid; a dancer sign-in never learns her uid, so
 * hers is this fixed word. Only the claim id unlocks a preview.
 */
export const DANCER_PUSH_PREVIEW_LABEL = 'dancer-sign-in';

export interface DancerPushDeviceRecord {
  token: string;
  claimId: string;
}

function validRecord(value: unknown): value is DancerPushDeviceRecord {
  const row = value as Partial<DancerPushDeviceRecord> | null;
  return !!row && typeof row.token === 'string' && row.token.length >= 20
    && typeof row.claimId === 'string' && /^[A-Za-z0-9._:-]{16,128}$/.test(row.claimId);
}

/** The token this device last registered for a dancer sign-in, if any. */
export function readDancerPushDevice(): DancerPushDeviceRecord | null {
  try {
    const raw = window.localStorage?.getItem(DEVICE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return validRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Fired on this window when the record changes, so the dancer home can start
 * (or stop) listening for a push that lands while she is looking at it.
 */
export const DANCER_PUSH_DEVICE_EVENT = 'ldg-dancer-push-device';

function announceDeviceChange(): void {
  try { window.dispatchEvent(new Event(DANCER_PUSH_DEVICE_EVENT)); }
  catch { /* No window, nothing listening. */ }
}

export function rememberDancerPushDevice(record: DancerPushDeviceRecord): void {
  try { window.localStorage?.setItem(DEVICE_KEY, JSON.stringify(record)); }
  catch { /* The server is the record; a blocked store only costs the On check next time. */ }
  announceDeviceChange();
}

export function forgetDancerPushDevice(): void {
  try { window.localStorage?.removeItem(DEVICE_KEY); }
  catch { /* Nothing to forget. */ }
  announceDeviceChange();
}

/**
 * SIGN OUT TAKES THIS PHONE OFF HER (review round 1, 2026-10-19). Signing
 * out deletes her session, and the delivery fence already refuses a token
 * bound to a session that is gone, so no push could reach this phone anyway.
 * This is the tidy half, on a shared device: the token comes off her users
 * doc and its owner row is released while her session is still live (one
 * portal call, bounded by a short wait so a slow network never holds up the
 * sign-out), and this browser forgets the device record and the service
 * worker's preview identity, so the next person on this phone inherits
 * nothing of hers. Every step is best effort and none of them throws.
 *
 * The calls are handed in so this file stays free of the session and
 * preview modules, and a test can run it in plain Node.
 */
export async function releaseDancerPushDeviceOnSignOut(deps: {
  csrfToken: string | null;
  remove: (csrfToken: string, pushToken: string) => Promise<unknown>;
  clearPreview: (claimId: string, label: string) => Promise<unknown>;
  waitMs?: number;
}): Promise<{ hadDevice: boolean }> {
  const device = readDancerPushDevice();
  forgetDancerPushDevice();
  if (!device) return { hadDevice: false };
  const within = (work: Promise<unknown>) => new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, deps.waitMs ?? 2_500);
    work.then(() => undefined, () => undefined).finally(() => { clearTimeout(timer); resolve(); });
  });
  const steps: Promise<unknown>[] = [];
  if (deps.csrfToken) {
    try { steps.push(deps.remove(deps.csrfToken, device.token)); }
    catch { /* The fence still refuses the token once the session is gone. */ }
  }
  try { steps.push(deps.clearPreview(device.claimId, DANCER_PUSH_PREVIEW_LABEL)); }
  catch { /* A blocked store only means generic copy on a later push. */ }
  await Promise.all(steps.map(within));
  return { hadDevice: true };
}

export function newDancerPushClaimId(): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `claim-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** Can this phone get Hub alerts at all, and if not, what to say. */
export function dancerPushGuidance(): PushDeviceGuidance {
  const hasNavigator = typeof navigator !== 'undefined';
  const hasWindow = typeof window !== 'undefined';
  const standalone = hasWindow && hasNavigator
    && (window.matchMedia?.('(display-mode: standalone)').matches
      || (navigator as unknown as { standalone?: boolean }).standalone === true);
  return pushDeviceGuidance({
    userAgent: hasNavigator ? navigator.userAgent : '',
    platform: hasNavigator ? navigator.platform : '',
    maxTouchPoints: hasNavigator ? navigator.maxTouchPoints : 0,
    standalone: Boolean(standalone),
    pushApiAvailable: hasNavigator
      && 'serviceWorker' in navigator
      && typeof Notification !== 'undefined'
      && typeof PushManager !== 'undefined',
  });
}

export type DancerPushView =
  | 'checking'
  | 'on'
  | 'off'
  | 'on-via-staff'
  | 'staff-phone'
  | 'phone-in-use'
  | 'parent-off'
  | 'blocked'
  | 'ios-home-screen'
  | 'legacy-ios'
  | 'unsupported';

/**
 * Where the server says this phone stands (lib/dancerSession.ts
 * DancerPushServerState, minus the promoted Jr Staff case, which the switch
 * handles before it asks). Spelled here so this file stays free of the
 * session module.
 */
export type DancerPushPhoneState = 'on' | 'off' | 'on_via_staff' | 'staff_phone' | 'phone_in_use' | 'parent_off';

/**
 * What the switch shows, from the facts it has. Pure, so a test can walk
 * every case without a browser.
 */
export function dancerPushView(input: {
  guidance: PushDeviceGuidance;
  permission: NotificationPermission | 'unavailable';
  phone: DancerPushPhoneState | null;
}): DancerPushView {
  if (input.guidance === 'ios-needs-home-screen') return 'ios-home-screen';
  if (input.guidance === 'legacy-ios') return 'legacy-ios';
  if (input.guidance === 'unsupported' || input.permission === 'unavailable') return 'unsupported';
  if (input.phone === null) return 'checking';
  // Another account's alerts on this phone are true whatever this browser's
  // permission says to her. A grown-up's phone offers her no switch; her own
  // staff account's phone offers Turn off, which takes off her record of it
  // (DECISIONS 150).
  if (input.phone === 'on_via_staff') return 'on-via-staff';
  // Her staff account's phone, not yet on for her dancer home: never a plain
  // Off, because it still gets her Jr Staff alerts (refute P3-3), and Turn on
  // adds her replies and to-dos.
  if (input.phone === 'staff_phone') return 'staff-phone';
  if (input.phone === 'phone_in_use') return 'phone-in-use';
  // Her parent's off switch (DECISIONS 150) wins over anything this browser
  // says, and she gets no button to undo it.
  if (input.phone === 'parent_off') return 'parent-off';
  if (input.phone === 'on' && input.permission === 'granted') return 'on';
  if (input.permission === 'denied') return 'blocked';
  return 'off';
}

/**
 * A promoted Jr Staff sign-in. Her Big/Little notes come with her staff
 * alerts, and those are turned on per phone in Account: a phone she turned
 * on here BEFORE she was promoted is not on for her staff alerts until she
 * does (notify.mjs skips a token bound to a dancer session), so the copy says
 * what to do rather than that it is done.
 */
export const DANCER_PUSH_STAFF_COPY =
  'Your Big/Little notes come with your Jr Staff alerts. To get them on this phone, open Account on this phone and turn on phone alerts there.';

/**
 * The shared-phone rule, said where she taps (lib/dancerSignInPush.mjs, who
 * may take a phone).
 */
export const DANCER_PUSH_SHARED_PHONE_COPY =
  'Sharing this phone or tablet with a brother or sister who signs in as a dancer here? It buzzes for whichever of you turned it on last. A phone that already gets Hub alerts for anyone else\'s account stays theirs.';

/**
 * Her words for each state, B&B voice, no dashes. Since 2026-10-24 the
 * switch sits on her home and covers her class boards as well as her
 * Big/Little notes (lib/dancerSignInPush.mjs, DANCER_SIGN_IN_PUSH_LANES).
 */
export const DANCER_PUSH_COPY: Record<DancerPushView, string> = {
  checking: 'Checking this phone...',
  on: 'On for this phone. You get a buzz for new posts and videos in your classes, and when someone in your Big/Little group writes.',
  off: 'Off for this phone.',
  // Her tap recorded this phone for her dancer home (DECISIONS 150, Bobbie's
  // "1": "yes, buzz that phone and open her dancer home"), so her replies and
  // to-dos buzz it through her staff account, this phone only, and only as
  // that account's own alert settings allow (refute P3-2).
  'on-via-staff': 'This phone already gets your Jr Staff alerts through your staff account. Your Big/Little notes come there too, as long as Big/Little alerts are on in that account. Replies and to-dos on your dancer home buzz this phone too, as long as alerts are on in that account, and tapping one opens it right here on your home.',
  'staff-phone': 'This phone already gets your Jr Staff alerts through your staff account. Your Big/Little notes come there too, as long as Big/Little alerts are on in that account. Tap Turn on to get your replies and to-dos from your dancer home here too.',
  'phone-in-use': 'This phone already gets Hub alerts for someone else\'s account, so it stays theirs. Your new posts and notes still show on your home. To get your own buzz, use your own phone or tablet.',
  'parent-off': 'Your grown-up turned off phone alerts for you. Your new posts and notes still show here on your home. Ask them if you want the buzz back.',
  blocked: 'Notifications for the Hub are turned off in this phone\'s settings. Turn them on there, then tap the button again.',
  'ios-home-screen': 'On an iPhone or iPad, add the Hub to your Home Screen first: tap Share, then Add to Home Screen, then open the Hub from that icon and turn this on.',
  'legacy-ios': 'This iPhone or iPad needs an update to get Hub alerts. Ask a grown-up to update it, then try again.',
  unsupported: 'This browser cannot get Hub alerts. Your new posts and notes still show here on your home.',
};
